import {
  all, call, put, select, take,
} from 'redux-saga/effects';
import {
  ceil, compact, compose,
  filter, find, get,
  identity, includes, map,
  pickBy, range, reduce,
  some, toNumber,
} from 'lodash/fp';
import * as errorActions from '../errors/actions';
import * as newContactsServices from '../new-contacts/services';
import * as actions from './actions';
import { XLSXExporter } from './modules';
import * as services from './services';

export function* fetchContact() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_CONTACT_REQUEST);

      const response = yield call(services.fetchContact, action.payload);

      const contact = response.data;

      yield put(actions.fetchContactSuccess({ contact }));
    } catch (error) {
      yield put(actions.fetchContactFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* fetchContacts() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_CONTACTS_REQUEST);

      const response = yield call(services.fetchContactsBySchool, action.payload);

      const contacts = {
        rows: get('data', response),
        total: get('headers.total', response),
        perPage: get('headers.per-page', response),
      };

      yield put(actions.fetchContactsSuccess({ contacts }));
    } catch (error) {
      yield put(actions.fetchContactsFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* fetchContactsCount() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_CONTACTS_COUNT_REQUEST);

      const response = yield call(services.fetchContactsCount, action.payload);

      const contactsCount = response.data;

      yield put(actions.fetchContactsCountSuccess({ contactsCount }));
    } catch (error) {
      yield put(actions.fetchContactsCountFailure({ error }));
    }
  }
}

export function* fetchContactsByClazzGroup() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_CONTACTS_BY_CLAZZ_GROUP_REQUEST);

      const response = yield call(services.fetchContactsByClazzGroup, action.payload);

      const contacts = {
        rows: response.data,
        total: response.headers.total,
        perPage: response.headers['per-page'],
      };

      yield put(actions.fetchContactsByClazzGroupSuccess({ contacts }));
    } catch (error) {
      yield put(actions.fetchContactsByClazzGroupFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* fetchContactsByCustomGroup() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_CONTACTS_BY_CUSTOM_GROUP_REQUEST);

      const response = yield call(services.fetchContactsByCustomGroup, action.payload);

      const contacts = {
        rows: response.data,
        total: response.headers.total,
        perPage: response.headers['per-page'],
      };

      yield put(actions.fetchContactsByCustomGroupSuccess({ contacts }));
    } catch (error) {
      yield put(actions.fetchContactsByCustomGroupFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* fetchGroups() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_GROUPS_REQUEST);

      const [clazzGroups, customGroups] = yield all([
        call(services.fetchClazzGroups, action.payload),
        call(services.fetchCustomGroups, action.payload),
      ]);

      const groups = {
        clazzGroups: get('data', clazzGroups),
        customGroups: get('data', customGroups),
      };

      yield put(actions.fetchGroupsSuccess({ groups }));
    } catch (error) {
      yield put(actions.fetchGroupsFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* createClazzGroup() {
  while (true) {
    try {
      const action = yield take(actions.CREATE_CLAZZ_GROUP_REQUEST);

      const response = yield call(services.createClazzGroup, action.payload);

      const clazzGroup = response.data;

      yield put(actions.createClazzGroupSuccess({ clazzGroup }));
    } catch (error) {
      yield put(actions.createClazzGroupFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* createCustomGroup() {
  while (true) {
    try {
      const action = yield take(actions.CREATE_CUSTOM_GROUP_REQUEST);

      const response = yield call(services.createCustomGroup, action.payload);

      const customGroup = response.data;

      yield put(actions.createCustomGroupSuccess({ customGroup }));
    } catch (error) {
      yield put(actions.createCustomGroupFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* exchangeGuardianContacts() {
  while (true) {
    try {
      const action = yield take(actions.EXCHANGE_GUARDIAN_CONTACTS_REQUEST);

      const { primaryGuardian, secondaryGuardian } = action.payload;

      const [
        secondaryGuardianContact,
        primaryGuardianContact,
      ] = yield all([
        call(services.updateGuardianContact, {
          gcid: primaryGuardian,
          level: 'secondary',
        }),
        call(services.updateGuardianContact, {
          gcid: secondaryGuardian,
          level: 'primary',
        }),
      ]);

      yield put(actions.exchangeGuardianContactsSuccess({
        primaryGuardianContact: primaryGuardianContact.data,
        secondaryGuardianContact: secondaryGuardianContact.data,
      }));
    } catch (error) {
      yield put(actions.exchangeGuardianContactsFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* updateContact() {
  while (true) {
    try {
      const action = yield take(actions.UPDATE_CONTACT_REQUEST);

      const { values } = action.payload;

      const clazzGroup = compose(
        get('value'),
        find(group => group.type === 'clazzGroup'),
      )(values.student.groups);
      const customGroups = compose(
        map(group => group.value),
        filter(group => group.type === 'customGroup'),
      )(values.student.groups);

      const contact = yield select(state => state.contacts.contact);

      const oldCustomGroups = compose(
        map(group => group.id),
        filter(group => !includes(group.id, customGroups)),
        get('custom_groups'),
      )(contact.data);
      const newCustomGroups = compose(
        map(group => group.value),
        filter(group => (
          group.type === 'customGroup'
          && compose(
            groups => !includes(group.value, groups),
            map(customGroup => customGroup.id),
            get('custom_groups'),
          )(contact.data)
        )),
      )(values.student.groups);

      const { id, ...inputs } = values.secondaryGuardian;

      const [
        student,
        primaryGuardian,
        secondaryGuardian,
      ] = yield all(compact([
        call(services.updateContact, {
          clazzGroup,
          customGroups,
          cid: values.student.id,
          clazzNumber: values.student.clazzNumber,
          email: values.student.email,
          name: values.student.name,
          phoneNumber: values.student.phoneNumber,
        }),
        call(services.updateGuardianContact, {
          gcid: values.primaryGuardian.id,
          email: values.primaryGuardian.email,
          name: values.primaryGuardian.name,
          relationship: values.primaryGuardian.relationship,
          phoneNumber: values.primaryGuardian.phoneNumber,
        }),
        values.secondaryGuardian.id
          ? call(...(some(identity, inputs)
            ? [services.updateGuardianContact, {
              gcid: values.secondaryGuardian.id,
              email: values.secondaryGuardian.email,
              name: values.secondaryGuardian.name,
              relationship: values.secondaryGuardian.relationship || 'etc',
              phoneNumber: values.secondaryGuardian.phoneNumber,
            }]
            : [services.deleteGuardianContact, {
              gcid: values.secondaryGuardian.id,
            }]
          ))
          : some(identity, inputs) && call(newContactsServices.createGuardianContact, {
            cid: values.student.id,
            level: 'secondary',
            email: values.secondaryGuardian.email,
            name: values.secondaryGuardian.name,
            phoneNumber: values.secondaryGuardian.phoneNumber,
            relationship: values.secondaryGuardian.relationship,
          }),
        ...map(customGroup => call(services.deleteGroupRelationship, { cid: get('id', contact.data), gid: customGroup }), oldCustomGroups),
        ...map(customGroup => call(services.createGroupRelationship, { cid: get('id', contact.data), gid: customGroup }), newCustomGroups),
      ]));

      const newContact = {
        ...student.data,
        primary_guardian_contact: primaryGuardian.data,
        secondary_guardian_contact: some(identity, inputs) ? secondaryGuardian.data : null,
      };

      yield put(actions.updateContactSuccess({ contact: newContact }));
    } catch (error) {
      yield put(actions.updateContactFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* fetchContactsAllBySchool() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_CONTACTS_ALL_BY_SCHOOL_REQUEST);

      const response = yield call(services.fetchContactsBySchool, action.payload);

      const responses = yield all(compose(
        map(number => call(services.fetchContactsBySchool, { ...action.payload, pageNumber: number + 1 })),
        range(1),
        ceil,
      )(response.headers.total / response.headers['per-page'] || 1));

      const allContacts = [
        ...response.data,
        ...reduce((acc, cur) => [...acc, ...cur.data], [], responses),
      ];

      yield put(actions.fetchContactsAllBySchoolSuccess({ allContacts }));
    } catch (error) {
      yield put(actions.fetchContactsAllBySchoolFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* fetchContactsAllByClazzGroup() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_CONTACTS_ALL_BY_CLAZZ_GROUP_REQUEST);

      const response = yield call(services.fetchContactsByClazzGroup, action.payload);

      const responses = yield all(compose(
        map(number => call(services.fetchContactsByClazzGroup, { ...action.payload, pageNumber: number + 1 })),
        range(1),
        ceil,
      )(response.headers.total / response.headers['per-page'] || 1));

      const allContacts = [
        ...response.data,
        ...reduce((acc, cur) => [...acc, ...cur.data], [], responses),
      ];

      yield put(actions.fetchContactsAllByClazzGroupSuccess({ allContacts }));
    } catch (error) {
      yield put(actions.fetchContactsAllByClazzGroupFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* fetchContactsAllByCustomGroup() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_CONTACTS_ALL_BY_CUSTOM_GROUP_REQUEST);

      const response = yield call(services.fetchContactsByCustomGroup, action.payload);

      const responses = yield all(compose(
        map(number => call(services.fetchContactsByCustomGroup, { ...action.payload, pageNumber: number + 1 })),
        range(1),
        ceil,
      )(response.headers.total / response.headers['per-page'] || 1));

      const allContacts = [
        ...response.data,
        ...reduce((acc, cur) => [...acc, ...cur.data], [], responses),
      ];

      yield put(actions.fetchContactsAllByCustomGroupSuccess({ allContacts }));
    } catch (error) {
      yield put(actions.fetchContactsAllByCustomGroupFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

function* deleteContact(contactId) {
  try {
    yield call(services.deleteContact, { cid: contactId });
  } catch {
    yield put(actions.pushFailedContactsDeletedId({ contactId }));
  }
}

export function* deleteContacts() {
  while (true) {
    try {
      const action = yield take(actions.DELETE_CONTACTS_REQUEST);

      const { cids } = action.payload;

      yield all(map(cid => deleteContact(cid), cids));

      yield put(actions.deleteContactsSuccess());
    } catch (error) {
      yield put(actions.deleteContactsFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* deleteContactsAll() {
  while (true) {
    try {
      const action = yield take(actions.DELETE_CONTACTS_ALL_REQUEST);

      const { sid } = action.payload;

      yield call(services.deleteContacts, { sid });

      yield put(actions.deleteContactsAllSuccess());
    } catch (error) {
      yield put(actions.deleteContactsAllFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* applyChangedGroups() {
  while (true) {
    try {
      const action = yield take(actions.APPLY_CHANGED_GROUPS_REQUEST);

      const groups = yield select(state => state.contacts.groups);

      const { values } = action.payload;

      const updatedGroups = {
        clazzGroups: pickBy((clazzGroup, key) => (
          toNumber(clazzGroup) !== get('class_name', find(group => group.id === toNumber(key), get('clazzGroups', groups.data)))
        ), values.updated.clazzGroups),
        customGroups: pickBy((customGroup, key) => (
          customGroup !== get('name', find(group => group.id === toNumber(key), get('customGroups', groups.data)))
        ), values.updated.customGroups),
      };

      yield all([
        ...map.convert({ cap: false })((clazzGroup, key) => call(
          services.updateClazzGroup,
          { gid: toNumber(key), clazzName: clazzGroup },
        ), updatedGroups.clazzGroups),
        ...map.convert({ cap: false })((customGroup, key) => call(
          services.updateCustomGroup,
          { gid: toNumber(key), name: customGroup },
        ), updatedGroups.customGroups),
        ...map(clazzGroup => call(services.deleteClazzGroup, { gid: clazzGroup }), values.removed.clazzGroups),
        ...map(customGroup => call(services.deleteCustomGroup, { gid: customGroup }), values.removed.customGroups),
      ]);

      const [clazzGroups, customGroups] = yield all([
        call(services.fetchClazzGroups, action.payload),
        call(services.fetchCustomGroups, action.payload),
      ]);

      const newGroups = {
        clazzGroups: get('data', clazzGroups),
        customGroups: get('data', customGroups),
      };

      yield put(actions.applyChangedGroupsSuccess({ groups: newGroups }));
    } catch (error) {
      yield put(actions.applyChangedGroupsFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* exportContacts() {
  while (true) {
    try {
      const action = yield take(actions.EXPORT_CONTACTS);

      yield call(XLSXExporter.exportContacts, action.payload);
    } catch (error) {
      yield yield put(errorActions.errorOccured({ error }));
    }
  }
}
